import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Paperclip, X } from 'lucide-react';
import { useI18n } from '../../context/I18nContext';
import { formatStorageFromBytes } from '../../utils/storageQuota';
import {
  DROPDOWN_MENU_ANIMATION,
  DROPDOWN_MENU_ITEM,
  DROPDOWN_MENU_ITEM_DISABLED,
  DROPDOWN_MENU_ITEM_SELECTED,
  DROPDOWN_MENU_KEYFRAMES,
  DROPDOWN_MENU_PAPER,
} from '../ui/dropdownMenuStyles';
import Tooltip from '../ui/Tooltip';

export type AttachableProjectDoc = {
  id: string;
  file_name: string;
  mime_type?: string | null;
  size_bytes?: number | null;
};

interface ProjectAttachMenuProps {
  documents: AttachableProjectDoc[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  maxCount?: number;
  loading?: boolean;
  disabled?: boolean;
}

const MENU_WIDTH = 320;

const ProjectAttachMenu: React.FC<ProjectAttachMenuProps> = ({
  documents,
  selectedIds,
  onChange,
  maxCount = 5,
  loading = false,
  disabled = false,
}) => {
  const { t, language } = useI18n();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  const selectedSet = new Set(selectedIds);
  const selectedDocs = documents.filter((d) => selectedSet.has(d.id));
  const hasSelection = selectedDocs.length > 0;

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const menuWidth = Math.min(MENU_WIDTH, window.innerWidth - 16);
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - menuWidth - 8);
    const menuHeight = menu?.offsetHeight || Math.min(360, 56 + documents.length * 52);
    // Prefer above the trigger so the menu does not cover the AI rewrite block
    let top = rect.top - menuHeight - 6;
    if (top < 8) {
      top = Math.min(rect.bottom + 6, window.innerHeight - menuHeight - 8);
    }
    setPosition({ top: Math.max(8, top), left });
  }, [documents.length]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, selectedIds.length, documents.length, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => updatePosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (id: string) => {
    if (selectedSet.has(id)) {
      onChange(selectedIds.filter((x) => x !== id));
      return;
    }
    if (selectedIds.length >= maxCount) return;
    onChange([...selectedIds, id]);
  };

  const remove = (id: string) => {
    onChange(selectedIds.filter((x) => x !== id));
  };

  if (disabled && selectedDocs.length === 0) return null;
  if (!loading && documents.length === 0 && selectedDocs.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5 min-w-0 max-w-full">
      {/* Paperclip first = stable menu anchor; chips grow to the right */}
      {!disabled && (
        <div className="relative shrink-0">
          <Tooltip
            content={t('messages.attachFile')}
            disabled={!hasSelection || open}
          >
            <button
              ref={triggerRef}
              type="button"
              onClick={() => setOpen((v) => !v)}
              disabled={loading || documents.length === 0}
              className={`inline-flex items-center justify-center rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 text-gray-600 hover:text-gray-800 hover:bg-gray-100 disabled:opacity-40 disabled:pointer-events-none ${
                hasSelection
                  ? 'p-1.5'
                  : 'px-2.5 py-1.5 gap-1.5 text-sm'
              }`}
              aria-expanded={open}
              aria-haspopup="menu"
              aria-label={t('messages.attachFile')}
            >
              <Paperclip className="w-4 h-4 shrink-0" aria-hidden />
              {!hasSelection && <span>{t('messages.attachFile')}</span>}
            </button>
          </Tooltip>

          {open &&
            typeof document !== 'undefined' &&
            createPortal(
              <>
                <style>{DROPDOWN_MENU_KEYFRAMES}</style>
                <div
                  ref={menuRef}
                  role="menu"
                  className={`${DROPDOWN_MENU_PAPER} fixed z-[3000]`}
                  style={{
                    top: position?.top ?? -9999,
                    left: position?.left ?? 0,
                    width: MENU_WIDTH,
                    maxWidth: 'min(320px, calc(100vw - 16px))',
                    animation: DROPDOWN_MENU_ANIMATION,
                    visibility: position ? 'visible' : 'hidden',
                  }}
                >
                  <div className="px-3.5 py-2.5 border-b border-gray-100">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {t('messages.attachFromProject')}
                    </p>
                  </div>
                  {loading ? (
                    <div className={`${DROPDOWN_MENU_ITEM} ${DROPDOWN_MENU_ITEM_DISABLED}`}>
                      {t('common.loading')}
                    </div>
                  ) : documents.length === 0 ? (
                    <div className={`${DROPDOWN_MENU_ITEM} ${DROPDOWN_MENU_ITEM_DISABLED}`}>
                      {t('messages.noProjectFilesToAttach')}
                    </div>
                  ) : (
                    <div className="py-1">
                      {documents.map((doc) => {
                        const checked = selectedSet.has(doc.id);
                        const atMax = !checked && selectedIds.length >= maxCount;
                        const sizeLabel =
                          typeof doc.size_bytes === 'number' && doc.size_bytes > 0
                            ? formatStorageFromBytes(doc.size_bytes, language)
                            : null;
                        return (
                          <button
                            key={doc.id}
                            type="button"
                            role="menuitemcheckbox"
                            aria-checked={checked}
                            disabled={atMax}
                            onClick={() => toggle(doc.id)}
                            className={`${DROPDOWN_MENU_ITEM} !h-auto !items-start py-2 ${
                              checked ? DROPDOWN_MENU_ITEM_SELECTED : ''
                            } ${atMax ? DROPDOWN_MENU_ITEM_DISABLED : ''}`}
                          >
                            <span
                              className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                                checked
                                  ? 'border-primary-600 bg-primary-600 text-white'
                                  : 'border-gray-300 bg-white'
                              }`}
                            >
                              {checked && <Check className="w-3 h-3" aria-hidden />}
                            </span>
                            <span className="min-w-0 flex-1 text-left">
                              <span className="block break-words leading-snug font-medium">
                                {doc.file_name}
                              </span>
                              {sizeLabel && (
                                <span className="block text-xs text-gray-400 mt-0.5 tabular-nums">
                                  {sizeLabel}
                                </span>
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {documents.length > 0 && (
                    <div className="px-3.5 py-2.5 border-t border-gray-100 text-xs text-gray-500 leading-snug">
                      {t('messages.attachLimitHint', { max: maxCount })}
                    </div>
                  )}
                </div>
              </>,
              document.body
            )}
        </div>
      )}

      {selectedDocs.map((doc) => {
        const sizeLabel =
          typeof doc.size_bytes === 'number' && doc.size_bytes > 0
            ? formatStorageFromBytes(doc.size_bytes, language)
            : null;
        const tip = sizeLabel ? `${doc.file_name} · ${sizeLabel}` : doc.file_name;
        return (
          <Tooltip key={doc.id} content={tip}>
            <span className="inline-flex items-center gap-1 max-w-[11rem] sm:max-w-[14rem] rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700">
              <span className="truncate min-w-0">{doc.file_name}</span>
              <button
                type="button"
                onClick={() => remove(doc.id)}
                className="shrink-0 p-0.5 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                aria-label={t('messages.detachFile', { name: doc.file_name })}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          </Tooltip>
        );
      })}
    </div>
  );
};

export default ProjectAttachMenu;
